import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Package, Truck, User, Check, ChevronDown, ChevronUp,
  Loader2, AlertTriangle, Navigation, Map, List, Eye, Save
} from "lucide-react";
import {
  buildDirectionsEmbedUrl,
  buildOverallSessionMapBlobUrl,
  buildGoogleMapsRouteLink,
  extractShipmentCoords,
  TRIP_HEX_COLORS,
} from "@/utils/mapHelpers";

const InwardPreview = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const {
    assignments = [],
    plan2Assignments: backendPlan2Assignments = [],
    trips = [],
    plan2Trips = [],
    availableRiders = [],
    wizardHubId,
    wizardHub,
    selectedFilters,
    riderCount,
    capacityPerRider,
    inputMode,
    bulkShipments,
    selectedRiders: initialSelectedRiders,
    initialRiderAssignments = {},
  } = state;

  const [activePlan, setActivePlan] = useState("plan1");
  // Plan 1 = TSP Chain-Split, Plan 2 = K-Means
  const [tspRiderAssignments, setTspRiderAssignments] = useState(initialRiderAssignments);
  const [kmeansRiderAssignments, setKmeansRiderAssignments] = useState(initialRiderAssignments);
  const [expandedTrips, setExpandedTrips] = useState({});
  const [tripViewModes, setTripViewModes] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [showOverallMap, setShowOverallMap] = useState(true);
  const [savePlanChoice, setSavePlanChoice] = useState(null);

  // TSP assignments come from backend (already enriched with reference_id, coordinates, etc.)
  const tspAssignments = backendPlan2Assignments;
  const hasPlan2 = tspAssignments.length > 0 && assignments.length > 0;

  // For single-plan flows (e.g. Pincode Trip Planning), use whichever set has data
  const singlePlanAssignments = hasPlan2
    ? null  // dual plan — handled by plan tabs
    : (assignments.length > 0 ? assignments : tspAssignments);

  // Plan 1 = TSP, Plan 2 = K-Means (only relevant when hasPlan2)
  const currentAssignments = hasPlan2
    ? (activePlan === "plan1" ? tspAssignments : assignments)
    : singlePlanAssignments;
  const currentRiderAssignments = hasPlan2
    ? (activePlan === "plan1" ? tspRiderAssignments : kmeansRiderAssignments)
    : (assignments.length > 0 ? kmeansRiderAssignments : tspRiderAssignments);
  const setCurrentRiderAssignments = hasPlan2
    ? (activePlan === "plan1" ? setTspRiderAssignments : setKmeansRiderAssignments)
    : (assignments.length > 0 ? setKmeansRiderAssignments : setTspRiderAssignments);

  // If no data, redirect back
  useEffect(() => {
    if (!assignments.length && !trips.length) {
      navigate("/pharmeasy/hub-ops");
    }
  }, [assignments, trips, navigate]);

  const hubInfo = useMemo(() => {
    if (wizardHub?.coordinates?.lat && wizardHub?.coordinates?.lng) {
      return { lat: wizardHub.coordinates.lat, lng: wizardHub.coordinates.lng, name: wizardHub.hub_name, address: wizardHub.address };
    }
    return null;
  }, [wizardHub]);

  // Overall session map URL for active plan
  const overallMapUrl = useMemo(() => {
    return buildOverallSessionMapBlobUrl(currentAssignments.length ? currentAssignments : trips, hubInfo);
  }, [currentAssignments, trips, hubInfo]);

  const getMapLink = (shipment) => {
    const coords = extractShipmentCoords(shipment);
    if (coords) return `https://www.google.com/maps?q=${coords.lat},${coords.lng}`;
    return null;
  };

  const formatAddress = (destAddr) => {
    if (!destAddr) return "—";
    const parts = [destAddr.address1, destAddr.city, destAddr.pincode].filter(Boolean);
    return parts.join(", ") || "—";
  };

  const saveInwardSession = async (planToSave) => {
    let riderMap, assignmentsToSave, planLabel;

    if (hasPlan2) {
      // Dual-plan: save whichever plan the user chose
      riderMap = planToSave === "plan1" ? tspRiderAssignments : kmeansRiderAssignments;
      assignmentsToSave = planToSave === "plan1" ? tspAssignments : assignments;
      planLabel = planToSave === "plan1" ? "TSP Chain-Split" : "K-Means Clustering";
    } else {
      // Single-plan (Pincode Trip Planning or only one plan generated)
      riderMap = assignments.length > 0 ? kmeansRiderAssignments : tspRiderAssignments;
      assignmentsToSave = singlePlanAssignments;
      planLabel = "Pincode Trip Planning";
    }

    const usedRiders = Object.values(riderMap).filter(Boolean);
    const duplicates = usedRiders.filter((id, idx) => usedRiders.indexOf(id) !== idx);
    if (duplicates.length > 0) {
      const dupNames = [...new Set(duplicates)].map(id => {
        const rider = availableRiders.find(r => r._id === id);
        return rider?.name || id;
      });
      toast.error(`Cannot save: Duplicate rider assignments (${dupNames.join(", ")})`);
      return;
    }

    setSaving(true);
    try {
      const enrichedAssignments = assignmentsToSave.map(a => ({
        ...a,
        rider_id: riderMap[a.trip_number] || a.rider_id || null,
      }));

      const res = await axios.post(`${API}/inward/${wizardHubId}/save`, {
        hub_id: wizardHubId,
        rider_count: parseInt(riderCount) || 0,
        capacity_per_rider: parseInt(capacityPerRider) || 0,
        filters_used: selectedFilters,
        assignments: enrichedAssignments,
        plan_used: planLabel,
      });

      setSaveResult(res.data);
      setSavePlanChoice(null);
      toast.success(`Inward Session ${res.data.session_code} created using ${planLabel}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save session");
    } finally {
      setSaving(false);
    }
  };

  const toggleTripView = (tripNum, mode) => {
    setTripViewModes(prev => ({ ...prev, [tripNum]: mode }));
  };

  // Duplicate check for current plan
  const usedRiders = Object.values(currentRiderAssignments).filter(Boolean);
  const hasDuplicates = usedRiders.length !== new Set(usedRiders).size;
  const duplicateRiderNames = hasDuplicates
    ? [...new Set(usedRiders.filter((id, idx) => usedRiders.indexOf(id) !== idx))].map(id => {
        const r = availableRiders.find(rider => rider._id === id);
        return r?.name || id;
      })
    : [];

  const totalShipments = currentAssignments.reduce((sum, a) => sum + (a.shipment_count || 0), 0);

  if (saveResult) {
    return (
      <PharmeasyLayout>
        <div className="max-w-2xl mx-auto py-16">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-emerald-700 mb-2">Inward Session Created!</h2>
            <p className="text-slate-600 mb-1">
              Session ID: <span className="font-mono font-semibold text-blue-600">{saveResult.session_code}</span>
            </p>
            <p className="text-sm text-slate-500 mb-6">{saveResult.total_shipments} shipments ready for scanning</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/pharmeasy/hub-ops")} data-testid="back-hub-ops-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Hub Ops
              </Button>
              {saveResult.session_id && (
                <Button
                  onClick={() => navigate(`/pharmeasy/hub-ops/session/${saveResult.session_id}`)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="view-session-btn"
                >
                  <Eye className="w-4 h-4 mr-2" /> View Session
                </Button>
              )}
            </div>
          </div>
        </div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout>
      <div className="space-y-6" data-testid="inward-preview-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="preview-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                Assignment Preview
              </h1>
              <p className="text-sm text-slate-500">
                {wizardHub?.hub_name || "Hub"} — {currentAssignments.length} trips, {totalShipments} shipments
              </p>
            </div>
          </div>
          {hasDuplicates && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-3 py-1.5 rounded-lg">
              <AlertTriangle className="w-4 h-4" />
              <span>Duplicate rider: {duplicateRiderNames.join(", ")}</span>
            </div>
          )}
        </div>

        {/* Plan Tabs */}
        {hasPlan2 && (
          <div className="flex items-center gap-4" data-testid="plan-tabs">
            <button
              onClick={() => setActivePlan("plan1")}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activePlan === "plan1"
                  ? "bg-slate-800 text-white shadow-md"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="plan1-tab"
            >
              Plan 1
              <Badge className={`text-xs ${activePlan === "plan1" ? "bg-slate-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                {tspAssignments.length} trips
              </Badge>
            </button>
            <button
              onClick={() => setActivePlan("plan2")}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activePlan === "plan2"
                  ? "bg-slate-800 text-white shadow-md"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="plan2-tab"
            >
              Plan 2
              <Badge className={`text-xs ${activePlan === "plan2" ? "bg-slate-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                {assignments.length} trips
              </Badge>
            </button>
          </div>
        )}

        {/* Overall Session Map */}
        <Card data-testid="overall-session-map">
          <div
            className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 border-b"
            onClick={() => setShowOverallMap(p => !p)}
          >
            <div className="flex items-center gap-2">
              <Map className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-slate-700">Overall Session Map</span>
              <Badge variant="outline" className="text-xs">{currentAssignments.length} trips</Badge>
              {hasPlan2 && (
                <Badge className="text-xs bg-slate-100 text-slate-700">
                  {activePlan === "plan1" ? "Plan 1" : "Plan 2"}
                </Badge>
              )}
            </div>
            {showOverallMap ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
          {showOverallMap && (
            <CardContent className="p-4">
              {overallMapUrl ? (
                <iframe
                  src={overallMapUrl}
                  width="100%"
                  height="480"
                  style={{ border: 0, borderRadius: 8 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Overall Session Map"
                  data-testid="overall-map-iframe"
                />
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">
                  <MapPin className="w-8 h-8 mr-2 opacity-50" /> No coordinates available
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Per-Trip Cards */}
        <div className="space-y-4">
          {currentAssignments.map((assignment, idx) => {
            const tripNum = assignment.trip_number;
            const isExpanded = expandedTrips[tripNum];
            const viewMode = tripViewModes[`${activePlan}_${tripNum}`] || "table";
            const shipments = assignment.sublocation_assignments || [];
            const assignedRiderId = currentRiderAssignments[tripNum];
            const assignedRider = availableRiders.find(r => r._id === assignedRiderId);
            const usedRiderIds = Object.entries(currentRiderAssignments)
              .filter(([tn]) => parseInt(tn) !== tripNum)
              .map(([, rid]) => rid);
            const tripColor = TRIP_HEX_COLORS[idx % TRIP_HEX_COLORS.length];

            return (
              <Card key={`${activePlan}-${tripNum}`} className="overflow-hidden" data-testid={`trip-card-${tripNum}`}>
                {/* Trip Header */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedTrips(p => ({ ...p, [tripNum]: !p[tripNum] }))}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tripColor }} />
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    <div>
                      <p className="font-semibold text-slate-800">Trip #{tripNum}</p>
                      <p className="text-xs text-slate-500">
                        Pincodes: <span className="font-medium text-slate-700">{assignment.pincodes_display || "—"}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Rider Dropdown */}
                    <div onClick={e => e.stopPropagation()}>
                      <Select
                        value={assignedRiderId || ""}
                        onValueChange={val => setCurrentRiderAssignments(p => ({ ...p, [tripNum]: val || null }))}
                      >
                        <SelectTrigger className="w-[180px] h-8 text-sm">
                          <SelectValue placeholder="Select Rider">
                            {assignedRider ? (
                              <span className="flex items-center gap-2"><User className="w-3 h-3" />{assignedRider.name}</span>
                            ) : "Select Rider"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {availableRiders.map(rider => {
                            const used = usedRiderIds.includes(rider._id);
                            return (
                              <SelectItem key={rider._id} value={rider._id} disabled={used} className={used ? "opacity-50" : ""}>
                                <span className="flex items-center gap-2">
                                  <User className="w-3 h-3" />{rider.name}
                                  {used && <span className="text-xs text-slate-400">(assigned)</span>}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">{assignment.location_name}</Badge>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-700">{assignment.shipment_count} shipments</p>
                      <div className="flex gap-2 text-xs">
                        {assignment.forward_count > 0 && <span className="text-blue-600">{assignment.forward_count} fwd</span>}
                        {assignment.return_count > 0 && <span className="text-purple-600">{assignment.return_count} ret</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && shipments.length > 0 && (
                  <div className="border-t">
                    {/* View Toggle */}
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                      <span className="text-xs text-slate-500">{shipments.length} shipments in sequence</span>
                      <Tabs value={viewMode} onValueChange={m => setTripViewModes(prev => ({ ...prev, [`${activePlan}_${tripNum}`]: m }))}>
                        <TabsList className="h-7">
                          <TabsTrigger value="table" className="text-xs h-6 px-2"><List className="w-3 h-3 mr-1" />Table</TabsTrigger>
                          <TabsTrigger value="map" className="text-xs h-6 px-2"><Map className="w-3 h-3 mr-1" />Map</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>

                    {viewMode === "table" ? (
                      /* Table View */
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Seq</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Shipment ID</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Package ID</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LBN</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pincode</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Address</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Map</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {shipments.map((sub, subIdx) => {
                              const mapLink = getMapLink(sub);
                              const isReturn = sub.is_return || sub.shipment_type === "RETURN";
                              return (
                                <tr key={subIdx} className={`hover:bg-white ${isReturn ? "bg-purple-50" : ""}`}>
                                  <td className="px-4 py-3 align-middle text-slate-500">{sub.sequence}</td>
                                  <td className="px-4 py-3 align-middle">
                                    <Badge className={`text-xs ${isReturn ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                      {sub.shipment_type || (isReturn ? "RETURN" : "FORWARD")}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 align-middle font-mono text-blue-600">{sub.shipment_code || "—"}</td>
                                  <td className="px-4 py-3 align-middle font-mono text-slate-600">{sub.package_id || "—"}</td>
                                  <td className="px-4 py-3 align-middle font-mono text-purple-600">{sub.lbn || "—"}</td>
                                  <td className="px-4 py-3 align-middle font-mono text-orange-600">{sub.drop_pincode || sub.dest_address?.pincode || "—"}</td>
                                  <td className="px-4 py-3 align-middle text-slate-600 max-w-[200px] truncate" title={formatAddress(sub.dest_address)}>
                                    {formatAddress(sub.dest_address)}
                                  </td>
                                  <td className="px-4 py-3 align-middle text-center">
                                    {mapLink ? (
                                      <a href={mapLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                                        <Navigation className="w-4 h-4 inline" />
                                      </a>
                                    ) : <span className="text-slate-300">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      /* Map View */
                      <TripMapView shipments={shipments} tripNum={tripNum} hubCoords={wizardHub?.coordinates} />
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Save Footer */}
        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg sticky bottom-4" data-testid="save-footer">
          {savePlanChoice ? (
            /* Plan selection confirmation */
            <div className="flex flex-col gap-3">
              <p className="font-semibold text-slate-800">Which plan do you want to save?</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => saveInwardSession("plan1")}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-800 font-medium transition-all"
                  data-testid="save-plan1-btn"
                >
                  {saving ? "Saving..." : "Plan 1"}
                  <span className="text-xs text-slate-500">({tspAssignments.length} trips)</span>
                </button>
                {hasPlan2 && (
                  <button
                    onClick={() => saveInwardSession("plan2")}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-800 font-medium transition-all"
                    data-testid="save-plan2-btn"
                  >
                    {saving ? "Saving..." : "Plan 2"}
                    <span className="text-xs text-slate-500">({assignments.length} trips)</span>
                  </button>
                )}
                <Button variant="ghost" onClick={() => setSavePlanChoice(null)} className="text-slate-500">Cancel</Button>
              </div>
            </div>
          ) : (
            /* Default footer */
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-emerald-800">Ready to Save Inward Plan</p>
                <p className="text-sm text-emerald-600">
                  {currentAssignments.length} trips &bull; {totalShipments} shipments
                  {hasPlan2 && <span className="text-slate-500 ml-2">— Viewing {activePlan === "plan1" ? "Plan 1" : "Plan 2"}</span>}
                </p>
                <p className="text-xs text-emerald-600 mt-1">Shipments will be inwarded later through scanning</p>
              </div>
              <Button
                onClick={() => hasPlan2 ? setSavePlanChoice(true) : saveInwardSession("plan1")}
                disabled={saving || hasDuplicates}
                className={`px-6 ${hasDuplicates ? "bg-gray-400" : "bg-emerald-600 hover:bg-emerald-700"}`}
                data-testid="save-session-btn"
                title={hasDuplicates ? "Fix duplicate rider assignments first" : ""}
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Inward Session</>}
              </Button>
            </div>
          )}
        </div>
      </div>
    </PharmeasyLayout>
  );
};

/**
 * Trip Map View sub-component. Renders the Google Maps embed for a single trip.
 */
function TripMapView({ shipments, tripNum, hubCoords }) {
  const embedUrl = buildDirectionsEmbedUrl(shipments, hubCoords);
  const routeLink = buildGoogleMapsRouteLink(shipments, hubCoords);

  if (embedUrl) {
    return (
      <div className="relative">
        <iframe
          src={embedUrl}
          width="100%"
          height="400"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={`Trip ${tripNum} Route Map`}
          className="w-full"
          data-testid={`trip-map-iframe-${tripNum}`}
        />
        {/* Delivery Sequence overlay */}
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-lg p-3 max-h-56 overflow-y-auto shadow-lg">
          <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
            <Truck className="w-4 h-4" /> Delivery Sequence
          </p>
          {shipments.slice(0, 15).map((sub, i) => (
            <div key={i} className="text-xs text-slate-600 flex items-center gap-2 py-0.5">
              <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
              <span className="truncate max-w-[180px]">{sub.shipment_code} - {sub.dest_address?.city || sub.drop_pincode}</span>
            </div>
          ))}
          {shipments.length > 15 && <p className="text-xs text-slate-400 mt-1">+{shipments.length - 15} more</p>}
        </div>
        {/* Open in Google Maps */}
        {routeLink && (
          <a
            href={routeLink}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-3 right-3 bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-700 flex items-center gap-1.5 shadow-lg"
          >
            <Navigation className="w-3 h-3" /> Open Full Route
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="h-64 flex items-center justify-center text-slate-400">
      <div className="text-center">
        <MapPin className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No coordinates available for map view</p>
      </div>
    </div>
  );
}

export default InwardPreview;
